import { useEffect, useRef } from "react";

export default function Hero() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".opacity-0-init").forEach((el) => {
              (el as HTMLElement).style.opacity = "1";
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20mobile%20telecommunications%20network%20technology%20dark%20navy%20blue%20glowing%20fiber%20optic%20data%20streams%20abstract%20digital%20connectivity%20cityscape%20futuristic%20infrastructure%20cinematic%20panoramic%20wide%20angle%20long%20exposure%20night%20photography&width=1920&height=1080&seq=inexx-hero-bg-01&orientation=landscape"
          alt="background"
          className="w-full h-full object-cover object-top"
        />
        {/* Overlay layers */}
        <div className="absolute inset-0 bg-navy/75" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/40 to-navy/80" />
        {/* Animated grid lines */}
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "linear-gradient(rgba(0,119,204,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,119,204,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px"
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 md:px-8 text-center">
        {/* Tag */}
        <div
          className="opacity-0-init animate-fade-in inline-flex items-center gap-2 border border-inexx-blue/50 bg-inexx-blue/10 rounded-full px-5 py-2 mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-inexx-blue-light animate-pulse block" />
          <span className="text-inexx-blue-light text-sm font-medium tracking-widest">
            移動体通信のプロフェッショナル
          </span>
        </div>

        {/* Main Catchphrase */}
        <h1 className="opacity-0-init animate-fade-up delay-200 text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
          つながる未来を、
          <br />
          <span className="blue-gradient-text">確かな技術で。</span>
        </h1>

        {/* Sub Text */}
        <p className="opacity-0-init animate-fade-up delay-300 text-gray-300 text-base md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
          株式会社iNeXXは、移動体通信インフラの設計から保守まで、
          <br className="hidden md:block" />
          一貫してご支援します。
        </p>

        {/* CTA Buttons */}
        <div className="opacity-0-init animate-fade-up delay-400 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => handleScroll("#services")}
            className="w-full sm:w-auto cursor-pointer whitespace-nowrap bg-inexx-blue hover:bg-inexx-blue-dark text-white font-semibold px-10 py-4 rounded-lg text-base transition-all duration-200 hover:scale-105"
            style={{ boxShadow: "0 0 30px rgba(0,119,204,0.4)" }}
          >
            事業内容を見る
          </button>
          <button
            onClick={() => handleScroll("#contact")}
            className="w-full sm:w-auto cursor-pointer whitespace-nowrap border border-white/50 hover:border-white text-white hover:bg-white/10 font-semibold px-10 py-4 rounded-lg text-base transition-all duration-200"
          >
            お問い合わせ
          </button>
        </div>

        {/* Scroll indicator */}
        <div className="opacity-0-init animate-fade-in delay-700 mt-8 md:mt-20 flex flex-col items-center gap-2">
          <span className="text-gray-500 text-xs tracking-widest">SCROLL</span>
          <div className="w-0.5 h-10 bg-gradient-to-b from-inexx-blue to-transparent" />
        </div>
      </div>

      {/* Decorative circles */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-inexx-blue/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full bg-inexx-blue/5 blur-3xl pointer-events-none" />
    </section>
  );
}
